import React from 'react';
import styled, { keyframes } from 'styled-components';

// ============================================================================
// TYPES
// ============================================================================

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'danger';
  isLoading?: boolean;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  animation: ${fadeIn} 0.15s ease-out;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideIn} 0.2s ease-out;
  overflow: hidden;
`;

const Content = styled.div`
  padding: 24px;
  text-align: center;
`;

const IconContainer = styled.div<{ variant: 'default' | 'danger' }>`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.variant === 'danger' ? '#fef2f2' : '#eff6ff'};
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    if (props.variant === 'danger') {
      return `
        background: #dc2626;
        color: white;
        border: none;
        &:hover:not(:disabled) { background: #b91c1c; }
      `;
    }
    if (props.variant === 'primary') {
      return `
        background: #008EA2;
        color: white;
        border: none;
        &:hover:not(:disabled) { background: #00788A; }
      `;
    }
    return `
      background: white;
      color: #374151;
      border: 1px solid #d1d5db;
      &:hover:not(:disabled) { background: #f9fafb; }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// SVG Icons
const QuestionIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#008EA2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const WarningIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'אישור',
  cancelText = 'ביטול',
  variant = 'default',
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Overlay>
      <Modal>
        <Content>
          <IconContainer variant={variant}>
            {variant === 'danger' ? <WarningIcon /> : <QuestionIcon />}
          </IconContainer>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Content>
        <Footer>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'מבצע...' : confirmText}
          </Button>
          {cancelText && (
            <Button onClick={onClose} disabled={isLoading}>
              {cancelText}
            </Button>
          )}
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ConfirmationModal;
